/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { createContext, useEffect } from "react";
import { useSelector } from "react-redux";
import i18n, { isRtlLanguage } from "@i18n";
import { RootState } from "@Reducers/index";

const LanguageContext = createContext({
  preferredLanguage: "en",
});

const isReactNative = typeof document === "undefined";

interface ProviderLanguageProps {
  /** ProviderApp uses this to flip `document.documentElement.dir`. */
  isRTLCallback?: (isRTL: boolean) => void;
  children?: React.ReactNode;
}

function ProviderLanguage(props: Readonly<ProviderLanguageProps>) {
  const preferredLanguage = useSelector((state: RootState) => state.preferredLanguage);

  useEffect(() => {
    const isRTL = isRtlLanguage(preferredLanguage);
    // ⚠️ `isRTLCallback` is not data the parent renders: it is a DOM write (`document.documentElement.dir`)
    // that this component deliberately does not own, so the file stays importable where `document` is
    // undefined (see `isReactNative`). Dropping the callback and letting ProviderApp read
    // `state.preferredLanguage` itself would need ProviderApp.tsx, and would split one transition —
    // dir flip + `changeLanguage` + localStorage — across two independent subscribers of the same slice,
    // which can paint RTL against the previous language's bundle. Any fix must move all four together.
    // eslint-disable-next-line react-effect/no-pass-data-to-parent -- unfixable here; see the adjacent note
    props.isRTLCallback?.(isRTL);
    // Only when it differs: i18next emits `languageChanged` even for the language it already has, and that hands
    // every `useTranslation` consumer a new `t`. Every nested <ProviderApp> mounts its own copy of this component
    // (UserNameTagComponent wraps each avatar in one), so without the check each avatar that mounts relabels the
    // whole app, and `t`-keyed grid columns re-apply at a moment set by when that avatar's lazy chunk arrived (see
    // the ⚠️ KNOWN BUG at SlickGrid1's columns-effect skip). The first call still runs: init sets no language.
    // fire-and-forget: an effect cannot await; i18next re-renders subscribers when the bundle lands,
    // and the localStorage write below is independent of it.
    if (i18n.language !== preferredLanguage) void i18n.changeLanguage(preferredLanguage);
    if (!isReactNative) {
      globalThis.localStorage.setItem("preferredLanguage", preferredLanguage);
    }
  }, [preferredLanguage]);

  const Provider = LanguageContext.Provider;
  return (
    <Provider
      value={{
        preferredLanguage,
      }}
    >
      {props.children}
    </Provider>
  );
}

export default ProviderLanguage;
